import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState, useCallback } from "react";
import { AppShell } from "@/components/app-shell";
import { fetchFeed } from "@/lib/posts";
import { PostCard, type FeedPost } from "@/components/post-card";
import { FeedSkeleton } from "@/components/feed-skeleton";
import { MessageSquare } from "lucide-react";

export const Route = createFileRoute("/discussions")({
  head: () => ({
    meta: [
      { title: "Xamseen — Discussions" },
      { name: "description", content: "Conversations from communities around the world — what people are noticing, debating and organising around." },
      { property: "og:title", content: "Xamseen — Discussions" },
      { property: "og:description", content: "Conversations from communities around the world on Xamseen." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: DiscussionsPage,
});

function DiscussionsPage() {
  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setPosts(await fetchFeed({ sort: "latest", postTypes: ["discussion", "announcement"], limit: 30 }));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  return (
    <AppShell>
      <div className="space-y-5">
        <header>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Discussions</h1>
          <p className="text-sm text-muted-foreground">What people are talking about in their communities.</p>
        </header>

        {loading ? (
          <FeedSkeleton />
        ) : posts.length === 0 ? (
          <div className="rounded-2xl border border-border p-12 text-center">
            <MessageSquare className="mx-auto mb-3 h-9 w-9 text-muted-foreground" />
            <p className="font-medium">No discussions yet</p>
            <p className="mt-1 text-sm text-muted-foreground">Start one from the post composer.</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {posts.map((p) => <PostCard key={p.id} post={p} onChanged={load} />)}
          </div>
        )}
      </div>
    </AppShell>
  );
}
